import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { formatKg } from '../../utils/coilMath';

/**
 * The coils on one order / challan / advice line, read-only.
 *
 * For a batch-tracked product the line's weight is an answer to "how much"; this is the
 * answer to "which rolls" — the question the whole feature exists to make answerable
 * after the fact. Renders nothing at all when the line has no coils, so every ordinary
 * line is untouched.
 *
 * Takes either shape the API returns: `coil_allocations` on an order or delivery line
 * (what was claimed or consumed) and `coil_entries` on a purchase advice line (what
 * arrived).
 */
const CoilLineChips = ({ item, max = 8 }) => {
  const { t } = useTranslation();
  const coils = (item && (item.coil_allocations || item.coil_entries)) || [];
  if (!Array.isArray(coils) || coils.length === 0) return null;

  const shown = coils.slice(0, max);
  const rest = coils.length - shown.length;
  // A sales line reports what it consumed off the coil; a purchase line what arrived on
  // it. Either way the operator is looking for batch and kg.
  const kgOf = (c) => c.consumed_weight_kg ?? c.take_kg ?? c.weight_kg ?? c.remaining_weight_kg;

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide"
            style={{ color: UI_COLORS.TEXT.MUTED }}>
        <FontAwesomeIcon icon={faLayerGroup} />
        {t('coils.challan.coilsLabel')}
      </span>
      {shown.map((coil, i) => (
        <span
          key={coil.allocation_id || coil.coil_id || i}
          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium"
          style={{
            color: UI_COLORS.TEXT.PRIMARY,
            backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.1),
          }}
          title={coil.colour || undefined}
        >
          <span className="font-bold">{coil.batch_number || coil.coil_id}</span>
          {kgOf(coil) !== null && kgOf(coil) !== undefined && (
            <span style={{ color: UI_COLORS.TEXT.SECONDARY }}>
              {formatKg(kgOf(coil))} {t('common.units.kg')}
            </span>
          )}
          {coil.colour && (
            <span style={{ color: UI_COLORS.TEXT.MUTED }}>{coil.colour}</span>
          )}
        </span>
      ))}
      {rest > 0 && (
        <span className="text-[11px] font-medium" style={{ color: UI_COLORS.TEXT.MUTED }}>
          {t('coils.challan.moreCoils', { count: rest })}
        </span>
      )}
    </div>
  );
};

export default CoilLineChips;
